/**
 * API Error Format
 *
 * The backend returns errors in a consistent JSON structure:
 * - _tag: Error type identifier for pattern matching/branching
 * - message: Human-readable description for display to users
 * - Additional fields may be present (field, action, providerCode, quoteId, etc.)
 *
 * @see API Error Format documentation
 */

/** New API error format (_tag + message) */
const newApiErrorSchema = {
  _tag: (v: unknown): v is string => typeof v === "string",
  message: (v: unknown): v is string => typeof v === "string",
};

/** Legacy Volume API error format */
const legacyVolumeErrorSchema = {
  statusCode: (v: unknown): v is number => typeof v === "number",
  errorMessage: (v: unknown): v is string => typeof v === "string",
};

function isNewApiError(
  body: unknown,
): body is { _tag: string; message: string } {
  if (!body || typeof body !== "object") return false;
  const o = body as Record<string, unknown>;
  return newApiErrorSchema._tag(o._tag) && newApiErrorSchema.message(o.message);
}

function isLegacyVolumeError(body: unknown): body is {
  statusCode: number;
  errorMessage: string;
  traceId?: string;
  source?: string;
} {
  if (!body || typeof body !== "object") return false;
  const o = body as Record<string, unknown>;
  return (
    legacyVolumeErrorSchema.statusCode(o.statusCode) &&
    legacyVolumeErrorSchema.errorMessage(o.errorMessage)
  );
}

/**
 * Parsed API error - supports both new (_tag, message) and legacy (statusCode, errorMessage) formats.
 * Use for display and branching.
 */
export interface ParsedApiError {
  /** Human-readable message for display. Always present. */
  message: string;
  /** Error type tag (new format only). Use for switch/branching. */
  tag?: string;
  /** HTTP status code (legacy format or from Response). */
  statusCode?: number;
  /** Trace ID for support (legacy format). */
  traceId?: string;
  /** Additional fields from the error body (e.g. field, quoteId, providerCode). */
  readonly [key: string]: unknown;
}

export const PROVIDER_ISSUE_MESSAGE =
  "We're having an issue with our provider. Please try again or contact support.";

export class ProviderIssueError extends Error {
  constructor(message = PROVIDER_ISSUE_MESSAGE) {
    super(message);
    this.name = "ProviderIssueError";
  }
}

/**
 * Parse an error response body into a consistent structure.
 * Supports both new API format (_tag, message) and legacy Volume API format.
 */
export function parseApiErrorBody(body: unknown): ParsedApiError {
  if (isNewApiError(body)) {
    const { _tag, message, ...rest } = body;
    return { message: message ?? "An error occurred", tag: _tag, ...rest };
  }
  if (isLegacyVolumeError(body)) {
    return {
      message: body.errorMessage,
      statusCode: body.statusCode,
      traceId: body.traceId,
      source: body.source,
    };
  }
  // Fallback for malformed or unknown format
  if (
    body &&
    typeof body === "object" &&
    "message" in body &&
    typeof (body as { message: unknown }).message === "string"
  ) {
    return { message: (body as { message: string }).message };
  }
  return { message: "An error occurred" };
}

/**
 * Parse error from a failed fetch Response.
 * Reads the response body as JSON and returns a ParsedApiError.
 */
export async function parseApiErrorFromResponse(
  response: Response,
): Promise<ParsedApiError> {
  let body: unknown;
  try {
    const text = await response.text();
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  const parsed = parseApiErrorBody(body);
  return {
    ...parsed,
    statusCode: parsed.statusCode ?? response.status,
  };
}

export function toProviderIssueError(
  error: ParsedApiError,
): ProviderIssueError | null {
  return error.tag === "ProviderError" ||
    error.tag === "ProviderAuthenticationError"
    ? new ProviderIssueError()
    : null;
}

/**
 * Extract a display message from any error (Error, ParsedApiError, or unknown).
 */
export function getMessageFromError(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }
  return "An error occurred";
}
